/*
 * Copyright 2026 Sony Corporation
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

import { createDocScene, type DocScene } from '../scene-doc/apply-to-babylon';
import { createMeasureMarkers, type MeasureMarkers } from '../scene/measure-markers';
import { createNodeGizmo, type GizmoMode, type NodeGizmo } from '../scene/node-gizmo';
import type { BabylonContext } from '../components/scene/BabylonCanvas';
import type { LibraryResolver } from '../scene-doc/load-library';
import type { SceneDoc, SceneOp, Transform, Vec3 } from '../scene-doc/schema';

export interface UseDocSceneOptions {
  doc: SceneDoc;
  selection: readonly string[];
  mode: GizmoMode;
  onSelect: (ids: readonly string[]) => void;
  /** A gizmo drag, already throttled; it arrives as the op the GUI would emit. */
  onDrag: (op: SceneOp) => void;
  /** An import starting, arriving or failing. A model is a download, not an instant. */
  onAssetState?: (nodeId: string, state: 'loading' | 'ready' | 'failed', error?: string) => void;
  /** Read once, when the editor attaches. */
  resolveLibrary?: LibraryResolver;
  /**
   * While set, a click in the viewport picks a point on a surface instead of
   * selecting: in the world, and in that node's asset frame (its file's units),
   * which is undefined until the asset has loaded.
   */
  measure?: { nodeId: string; onPoint: (world: Vector3, local: Vector3 | undefined) => void };
}

export interface UseDocSceneResult {
  /** Handed to `BabylonCanvas`, once the editor exists. */
  attach: (context: BabylonContext) => () => void;
  /** Puts the device back on something: the given nodes, or the whole scene. */
  frame: (nodeIds?: readonly string[]) => void;
  /** Where a drop at these client coordinates lands, in scene units. */
  dropPoint: (clientX: number, clientY: number) => Vec3 | undefined;
  /** Which node a drop at these client coordinates would land on. */
  dropTarget: (clientX: number, clientY: number) => string | undefined;
  /** See `DocScene.rescale`. */
  rescale: (assetId: string, scale: number) => void;
  /** Marks measured points in the viewport; none clears them. */
  showMarkers: (points: readonly Vector3[]) => void;
}

/**
 * The imperative bridge: React renders panels, the document drives the scene.
 *
 * Everything here flows one way — document to graph. A drag is the only input,
 * and it enters as an op rather than as a mesh position, so the graph is never
 * the thing anyone reads the truth from (devplan §3).
 */
export const useDocScene = ({
  doc,
  selection,
  mode,
  onSelect,
  onDrag,
  onAssetState,
  resolveLibrary,
  measure,
}: UseDocSceneOptions): UseDocSceneResult => {
  const [parts, setParts] = useState<{
    context: BabylonContext;
    docScene: DocScene;
    gizmo: NodeGizmo;
    markers: MeasureMarkers;
  }>();

  // Read by callbacks the gizmo keeps for its lifetime, so they cannot close
  // over a stale document or a stale selection. Kept current out of band,
  // because a new callback identity must not rebuild the scene.
  const latest = useRef({
    selection,
    onSelect,
    onDrag,
    onAssetState,
    resolveLibrary,
    measure,
    docScene: undefined as DocScene | undefined,
    frame: undefined as ((nodeIds?: readonly string[]) => void) | undefined,
  });

  useEffect(() => {
    latest.current = { ...latest.current, selection, onSelect, onDrag, onAssetState, measure };
  }, [selection, onSelect, onDrag, onAssetState, measure]);

  const attach = useMemo(
    () => (context: BabylonContext) => {
      // An import is a download, so at attach time a document made of models
      // has no geometry to frame yet. Framing again as they land is the
      // difference between opening a scene and opening an empty view — but only
      // until the first click, after which the device belongs to the user.
      let touched = false;
      const takeOver = context.editor.scene.onPointerObservable.add(() => {
        touched = true;
      });

      const frame = (nodeIds?: readonly string[]) => {
        const bounds = docScene.bounds(nodeIds);
        context.editor.frame(bounds, { alpha: -Math.PI / 2.4, beta: 1.05, radius: bounds.radius * 1.8 });
        gizmo.refit();
      };

      const docScene = createDocScene({
        scene: context.editor.scene,
        resolveLibrary: latest.current.resolveLibrary,
        onAssetState: (nodeId, state, error) => {
          if (state === 'ready') {
            // The import landed after the sync that asked for it, so the
            // editor's own key light has not been told about its meshes. The
            // document's lights are refreshed by the doc scene itself.
            context.editor.setShadowCasters(docScene.meshes());
            if (!touched) {
              frame();
            }
          }
          latest.current.onAssetState?.(nodeId, state, error);
        },
      });
      const gizmo = createNodeGizmo({
        editor: context.editor,
        onTransformChanged: (transform: Transform) => {
          const nodeId = latest.current.selection[0];
          if (nodeId) {
            latest.current.onDrag({ op: 'set_transform', node_id: nodeId, ...transform });
          }
        },
        onPicked: (mesh) => {
          if (latest.current.measure) {
            return;
          }
          const nodeId = latest.current.docScene?.nodeIdOf(mesh);
          latest.current.onSelect(nodeId ? [nodeId] : []);
        },
      });

      // A tap, not a pick: a splat has no triangles, so the scene's own picking never reports one.
      const scene = context.editor.scene;
      const tap = scene.onPointerObservable.add((info) => {
        const measuring = latest.current.measure;
        if (info.type !== PointerEventTypes.POINTERTAP || !measuring) {
          return;
        }
        const point = docScene.surfaceAt(scene.pointerX, scene.pointerY);
        if (!point) {
          return;
        }
        const own = docScene.assetFrame(measuring.nodeId);
        const local = own && Vector3.TransformCoordinates(point, own.computeWorldMatrix(true).clone().invert());
        measuring.onPoint(point, local);
      });
      const markers = createMeasureMarkers(scene);

      latest.current.docScene = docScene;
      latest.current.frame = frame;
      setParts({ context, docScene, gizmo, markers });

      return () => {
        latest.current.docScene = undefined;
        latest.current.frame = undefined;
        setParts(undefined);
        context.editor.scene.onPointerObservable.remove(takeOver);
        scene.onPointerObservable.remove(tap);
        markers.dispose();
        gizmo.dispose();
        docScene.dispose();
      };
    },
    []
  );

  useEffect(() => {
    if (!parts) {
      return;
    }
    parts.docScene.sync(doc);
    parts.context.editor.setShadowCasters(parts.docScene.meshes());
    // The editor's key light is for a scene with none of its own; leaving it on
    // over a document that brings lights washes everything to white.
    parts.context.editor.setKeyLight(!doc.nodes.some((node) => node.kind === 'light'));
  }, [parts, doc]);

  // Once per attachment, not once per edit: reframing on every change would
  // move the device out from under whoever is editing. It runs after the sync
  // above, which is why that effect is declared first.
  useEffect(() => {
    latest.current.frame?.();
  }, [parts]);

  useEffect(() => {
    if (!parts) {
      return;
    }
    // After the sync above, which rebuilds meshes and with them their outlines.
    parts.docScene.setSelection(selection);
    parts.gizmo.attach(parts.docScene.nodeOf(selection[0]));
  }, [parts, selection, doc]);

  useEffect(() => {
    parts?.gizmo.setMode(mode);
  }, [parts, mode]);

  const frame = useCallback((nodeIds?: readonly string[]) => latest.current.frame?.(nodeIds), []);

  /** Client coordinates to canvas coordinates, which is the space the scene picks in. */
  const onCanvas = useCallback(
    (clientX: number, clientY: number): [number, number] | undefined => {
      const canvas = parts?.context.editor.engine.getRenderingCanvas();
      if (!canvas) {
        return undefined;
      }
      const rect = canvas.getBoundingClientRect();
      return [clientX - rect.left, clientY - rect.top];
    },
    [parts]
  );

  const dropPoint = useCallback(
    (clientX: number, clientY: number) => {
      const at = onCanvas(clientX, clientY);
      return at && parts ? parts.docScene.pointAt(at[0], at[1]) : undefined;
    },
    [parts, onCanvas]
  );

  const dropTarget = useCallback(
    (clientX: number, clientY: number) => {
      const at = onCanvas(clientX, clientY);
      return at && parts ? parts.docScene.nodeIdAt(at[0], at[1]) : undefined;
    },
    [parts, onCanvas]
  );

  const rescale = useCallback((assetId: string, scale: number) => parts?.docScene.rescale(assetId, scale), [parts]);

  const showMarkers = useCallback(
    (points: readonly Vector3[]) => {
      const camera = parts?.context.editor.camera;
      if (!parts || !camera) {
        return;
      }
      // A dot a fixed share of the view, whatever the scene's size.
      const size = points.length > 0 ? 0.012 * Vector3.Distance(camera.globalPosition, points[0]) : 0;
      parts.markers.show(points, size);
    },
    [parts]
  );

  return { attach, frame, dropPoint, dropTarget, rescale, showMarkers };
};
